// src/components/CreateCompany.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderLogged from '../components/HeaderLogged'; // Используем уже готовый HeaderLogged
import Footer from '../components/Footer';
import { HelpCircle } from 'lucide-react';
import ZIP_INDEX from '../data/zip_indexs.json'
import AddressAutocomplete from './AddressAutocomplete';
import { EMAIL_IN_STREET_ERROR, looksLikeEmail } from '../utils/address';

const FIELD_LABELS = {
  name: 'Business name',
  business_type: 'Business type',
  EIN: 'EIN',
  street_address: 'Street address',
  zip_code: 'ZIP code',
  country: 'Country',
  city: 'City',
  state: 'State',
  signing_name: 'Signing authority name',
  signing_pin: 'Signing PIN',
  signing_phone: 'Signing phone',
  signing_job_title: 'Signing job title',
  signing_ssn: 'Signing SSN',
};

function formatApiError(data) {
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  const messages = [];
  for (const [key, val] of Object.entries(data)) {
    const arr = Array.isArray(val) ? val : [val];
    const label = key === 'non_field_errors' ? '' : `${FIELD_LABELS[key] || key}: `;
    for (const m of arr) {
      if (typeof m === 'string') messages.push(`${label}${m}`);
    }
  }
  return messages.length ? messages.join(' ') : null;
}

function CreateCompany() {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');

  const [businessType, setBusinessType] = useState('');

  const [EIN, setEIN] = useState('');
  const [EINError, setEINError] = useState('');

  const [streetAddress, setStreetAddress] = useState('');
  const [streetAddressError, setStreetAddressError] = useState('');

  const [zipCode, setZipCode] = useState('');
  const [zipCodeError, setZipCodeError] = useState('');

  const [country, setCountry] = useState('USA');
  const [countryError, setCountryError] = useState('');

  const [city, setCity] = useState('');
  const [cityError, setCityError] = useState('');

  const [state, setState] = useState('');
  const [stateError, setStateError] = useState('');

  const [signingName, setSigningName] = useState('');
  const [signingNameError, setSigningNameError] = useState('');

  const [signingPin, setSigningPin] = useState('');
  const [signingPinError, setSigningPinError] = useState('');

  const [signingSsn, setSigningSsn] = useState('');
  const [signingSsnError, setSigningSsnError] = useState('');

  const [signingPhone, setSigningPhone] = useState('');
  const [signingPhoneError, setSigningPhoneError] = useState('');

  const [signingJobTitle, setSigningJobTitle] = useState('');
  const [signingJobTitleError, setSigningJobTitleError] = useState('');

  const [thirdPartyAllowed] = useState(true);

  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  // USPS address validation
  const [uspsValidating, setUspsValidating] = useState(false);
  const [uspsSuggestion, setUspsSuggestion] = useState(null); // { streetAddress, city, state, ZIPCode } | null
  const [uspsNotFound, setUspsNotFound] = useState(false);
  const [uspsUnavailable, setUspsUnavailable] = useState(false);
  const [uspsAptInvalid, setUspsAptInvalid] = useState(false);
  const [uspsChecked, setUspsChecked] = useState(false);

  // Полная нормализованная строка адреса от USPS, включая apt/ste/unit, если он есть.
  // Предпочитаем streetAddressAbbreviation (USPS уже всё склеил), иначе руками
  // соединяем primary + secondary.
  const uspsFullStreet = (addr) => {
    if (!addr) return '';
    if (addr.streetAddressAbbreviation) return String(addr.streetAddressAbbreviation);
    const primary = addr.streetAddress || '';
    const secondary = addr.secondaryAddress || '';
    return [primary, secondary].filter(Boolean).join(' ').trim();
  };

  const navigate = useNavigate();

  // Regex patterns
  const einRegex = /^\d{2}-?\d{7}$/;         // EIN: 2 digits + optional dash + 7 digits
  const ssnRegex = /^\d{3}-?\d{2}-?\d{4}$/;   // SSN: 3-2-4 with optional dashes
  const zipRegex = /^\d{5}(-\d{4})?$/;        // ZIP: 5 or 5-4
  const phoneRegex = /^[0-9()+-\s]{10}$/;   // Phone: digits, +, (), -, spaces, length 7–20
  const pinRegex = /^\d{5}$/;                 // PIN: exactly 5 digits
  const streetRegex = /^[A-Za-z0-9]( ?[A-Za-z0-9\-\/])*$/; 
  const zip5 = (v) => v.trim().replace(/\D/g, '').slice(0, 5); // берём первые 5 цифр для lookup
  const norm = (s) =>
    (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ').replace(/\./g, '');
  
  const validate = () => {
    let valid = true;

    if (!name.trim()) {
      setNameError('This field is required');
      valid = false;
    } else {
      setNameError('');
    }

    if (!EIN.trim()) {
        setEINError('This field is required');
        valid = false;
      } else if (!/^\d{9}$/.test(EIN.trim())) {
        setEINError('EIN must be exactly 9 digits');
        valid = false;
      } else {
        setEINError('');
      }

    if (!country.trim()) {
      setCountryError('This field is required');
      valid = false;
    } else {
      setCountryError('');
    }

    if (!streetAddress.trim()) {
      setStreetAddressError('This field is required');
      valid = false;
    } else {
      setStreetAddressError('');
    }

    if (!state) {
      setStateError('Please select a state');
      valid = false;
    } else {
      setStateError('');
    }

    if (!zipCode.trim()) {
      setZipCodeError('This field is required');
      valid = false;
    } else if (!zipRegex.test(zipCode.trim())) {
      setZipCodeError('Invalid ZIP (e.g., 12345 or 12345-6789)');
      valid = false;
    } else {
      setZipCodeError('');
    }

    if (!city.trim()) {
      setCityError('This field is required');
      valid = false;
    } else {
      setCityError('');
    }
    if (!streetAddress.trim()) {
      setStreetAddressError('This field is required'); // ↑ пусто — не валидно
      valid = false;
    } else if (!streetRegex.test(streetAddress.trim())) {
      setStreetAddressError(
        'Invalid street address (letters/digits, single spaces, "-" and "/" only; must start with a letter or digit)'
      ); // ↑ не прошло паттерн
      valid = false;
    } else {
      setStreetAddressError('');
    }
    if (!signingName.trim()) {
      setSigningNameError('This field is required');
      valid = false;
    } else {
      setSigningNameError('');
    }

    if (!signingPhone.trim()) {
      setSigningPhoneError('This field is required');
      valid = false;
    } else if (!phoneRegex.test(signingPhone.trim())) {
      setSigningPhoneError('Invalid phone format');
      valid = false;
    } else {
      setSigningPhoneError('');
    }

    const pinVal = signingPin.trim();
    if (!pinVal) {
      setSigningPinError('PIN is required');
      valid = false;
    } else if (!pinRegex.test(pinVal)) {
      setSigningPinError('PIN must be 5 digits');
      valid = false;
    } else if (/^0+$/.test(pinVal)) {
      // IRS rejects an all-zeros Taxpayer PIN (rule R0000-031).
      setSigningPinError('PIN cannot be all zeros');
      valid = false;
    } else {
      setSigningPinError('');
    }

    if (signingSsn.trim() && !ssnRegex.test(signingSsn.trim())) {
      setSigningSsnError('Invalid SSN (e.g., 123-45-6789)');
      valid = false;
    } else {
      setSigningSsnError('');
    }

    if (!signingJobTitle.trim()) {
      setSigningJobTitleError('This field is required');
      valid = false;
    } else {
      setSigningJobTitleError('');
    }
    if (valid) {
      const z = zip5(zipCode);
      const ref = ZIP_INDEX[z];

      if (!ref) {
        setZipCodeError('ZIP code not found.');
        valid = false;
      } else {
        if (ref.state.toUpperCase() !== state.toUpperCase()) {
          setStateError(`State does not match the ZIP (${z}). Expected ${ref.state}.`);
          valid = false;
        }
        if (norm(ref.city) !== norm(city)) {
          setCityError(`City does not match the ZIP (${z}). Expected ${ref.city}.`);
          valid = false;
        }
      }
    }
    return valid;
  };

  const uspsAddrDiffers = (suggestion) => {
    const norm2 = (s) => (s || '').trim().toUpperCase();
    return (
      norm2(uspsFullStreet(suggestion)) !== norm2(streetAddress) ||
      norm2(suggestion.city) !== norm2(city) ||
      norm2(suggestion.state) !== norm2(state) ||
      norm2(suggestion.ZIPCode) !== norm2(zipCode.replace(/\D/g, '').slice(0, 5))
    );
  };

  const applyUspsSuggestion = () => {
    if (!uspsSuggestion) return;
    const fullStreet = uspsFullStreet(uspsSuggestion);
    setStreetAddress(fullStreet || streetAddress);
    setCity(uspsSuggestion.city || city);
    setState(uspsSuggestion.state || state);
    setZipCode(uspsSuggestion.ZIPCode || zipCode);
    setUspsSuggestion(null);
    setUspsNotFound(false);
    setUspsAptInvalid(false);
    setUspsChecked(true);
    // Return overrides so doSave can use them immediately (state is async)
    return {
      streetAddress: fullStreet || streetAddress,
      city: uspsSuggestion.city || city,
      state: uspsSuggestion.state || state,
      zipCode: uspsSuggestion.ZIPCode || zipCode,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setLoading(true);

    if (!validate()) {
      setFormError('Please fix the errors above.');
      setLoading(false);
      return;
    }

    // USPS address check (skip if already confirmed by user)
    if (!uspsChecked) {
      setUspsValidating(true);
      setUspsSuggestion(null);
      setUspsNotFound(false);
      setUspsUnavailable(false);
      setUspsAptInvalid(false);
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch('/api/validate-address/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            streetAddress: streetAddress.trim(),
            city: city.trim(),
            state,
            ZIPCode: zipCode.trim().replace(/\D/g, '').slice(0, 5),
          }),
        });
        if (resp.ok) {
          const data = await resp.json();
          const addr = data?.address || {};
          const dpv = data?.additionalInfo?.DPVConfirmation || data?.additionalInfo?.dpvConfirmation || '';
          // DPV: Y = primary+secondary OK, S = primary OK but secondary (apt) not confirmed,
          //      D = secondary missing where required, N = primary not found.
          if (dpv === 'N') {
            setUspsNotFound(true);
            setUspsValidating(false);
            setLoading(false);
            return;
          }
          if (dpv === 'S' || dpv === 'D') {
            setUspsAptInvalid(true);
            setUspsValidating(false);
            setLoading(false);
            return;
          }
          if (addr.streetAddress && uspsAddrDiffers(addr)) {
            setUspsSuggestion(addr);
            setUspsValidating(false);
            setLoading(false);
            return;
          }
          // Address confirmed by USPS — proceed to save
        } else if (resp.status === 404) {
          // USPS: address not found
          setUspsNotFound(true);
          setUspsValidating(false);
          setLoading(false);
          return;
        } else if (resp.status === 503) {
          // USPS service temporarily unavailable — silently skip
        } else {
          // Other backend error — warn user
          setUspsUnavailable(true);
          setUspsValidating(false);
          setLoading(false);
          return;
        }
      } catch (_) {
        // Network error — silently skip
      } finally {
        setUspsValidating(false);
      }
    }

    await doSave();
  };

  const doSave = async (overrides = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name,
        business_type: businessType,
        EIN,
        street_address: overrides.streetAddress ?? streetAddress,
        zip_code: overrides.zipCode ?? zipCode,
        country,
        city: overrides.city ?? city,
        state: overrides.state ?? state,
        signing_name: signingName,
        signing_pin: signingPin,
        signing_phone: signingPhone.replace(/\D/g, ''),
        signing_job_title: signingJobTitle,
        signing_ssn: EIN,
        third_party_allowed: true,
      };

      const response = await fetch('/api/companies/create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      let data = null;
      try { data = await response.json(); } catch (_) { /* no JSON body */ }
      if (!response.ok) {
        throw new Error(formatApiError(data) || 'Failed to create company.');
      }
      const newCompanyId = data.id;
      navigate(`/company/${newCompanyId}`);
    } catch (err) {
      setFormError(err.message || 'Failed to create company.');
    } finally {
      setLoading(false);
    }
  };

  // US States list
  const US_STATES = [
    { name: 'Alabama', value: 'AL' },
    { name: 'Alaska', value: 'AK' },
    { name: 'Arizona', value: 'AZ' },
    { name: 'Arkansas', value: 'AR' },
    { name: 'California', value: 'CA' },
    { name: 'Colorado', value: 'CO' },
    { name: 'Connecticut', value: 'CT' },
    { name: 'Delaware', value: 'DE' },
    { name: 'Florida', value: 'FL' },
    { name: 'Georgia', value: 'GA' },
    { name: 'Hawaii', value: 'HI' },
    { name: 'Idaho', value: 'ID' },
    { name: 'Illinois', value: 'IL' },
    { name: 'Indiana', value: 'IN' },
    { name: 'Iowa', value: 'IA' },
    { name: 'Kansas', value: 'KS' },
    { name: 'Kentucky', value: 'KY' },
    { name: 'Louisiana', value: 'LA' },
    { name: 'Maine', value: 'ME' },
    { name: 'Maryland', value: 'MD' },
    { name: 'Massachusetts', value: 'MA' },
    { name: 'Michigan', value: 'MI' },
    { name: 'Minnesota', value: 'MN' },
    { name: 'Mississippi', value: 'MS' },
    { name: 'Missouri', value: 'MO' },
    { name: 'Montana', value: 'MT' },
    { name: 'Nebraska', value: 'NE' },
    { name: 'Nevada', value: 'NV' },
    { name: 'New Hampshire', value: 'NH' },
    { name: 'New Jersey', value: 'NJ' },
    { name: 'New Mexico', value: 'NM' },
    { name: 'New York', value: 'NY' },
    { name: 'North Carolina', value: 'NC' },
    { name: 'North Dakota', value: 'ND' },
    { name: 'Ohio', value: 'OH' },
    { name: 'Oklahoma', value: 'OK' },
    { name: 'Oregon', value: 'OR' },
    { name: 'Pennsylvania', value: 'PA' },
    { name: 'Rhode Island', value: 'RI' },
    { name: 'South Carolina', value: 'SC' },
    { name: 'South Dakota', value: 'SD' },
    { name: 'Tennessee', value: 'TN' },
    { name: 'Texas', value: 'TX' },
    { name: 'Utah', value: 'UT' },
    { name: 'Vermont', value: 'VT' },
    { name: 'Virginia', value: 'VA' },
    { name: 'Washington', value: 'WA' },
    { name: 'West Virginia', value: 'WV' },
    { name: 'Wisconsin', value: 'WI' },
    { name: 'Wyoming', value: 'WY' },
    { name: 'District of Columbia', value: 'DC' },
  ];
  const handlePhoneChange = (e) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      setSigningPhone(digits);
      if (signingPhoneError) setSigningPhoneError('');
    };
  const handleEINChange = (e) => {
      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 9);
      setEIN(digitsOnly);
      if (EINError) setEINError('');
  };
  const sanitize = (value) => {
    let filtered = value.replace(/[^A-Za-z0-9\-\/ ]/g, '');
    if (filtered && !/^[A-Za-z0-9]/.test(filtered[0])) {
      filtered = filtered.slice(1);
    }
    return filtered;
  };
  
  const resetUsps = () => {
    setUspsChecked(false);
    setUspsSuggestion(null);
    setUspsNotFound(false);
    setUspsUnavailable(false);
    setUspsAptInvalid(false);
  };

  const handleStreetChange = (e) => {
    const raw = e.target.value;
    // Email не превращаем в «адрес» вырезанием символов — останавливаем и объясняем
    if (looksLikeEmail(raw)) {
      setStreetAddressError(EMAIL_IN_STREET_ERROR);
      return;
    }
    const clean = sanitize(raw).slice(0, 35);
    setStreetAddress(clean);
    if (streetAddressError) setStreetAddressError('');
    resetUsps();
  };

  const handleAddressSelected = ({ streetAddress: s, city: c, state: st, zip: z }) => {
    // Юзер выбрал подсказку — всегда коммитим распарсенные поля, иначе в street
    // останется formatted_address, которую onChange записал перед place_changed.
    setStreetAddress(sanitize(s || '').slice(0, 35));
    if (streetAddressError) setStreetAddressError('');
    setCity(c || '');
    if (cityError) setCityError('');
    setState(st || '');
    if (stateError) setStateError('');
    setZipCode(z || '');
    if (zipCodeError) setZipCodeError('');
    resetUsps();
  };

  return (
  <div className="min-h-screen flex flex-col bg-gray-100">
    <HeaderLogged />

    <main className="flex-grow flex justify-center items-start py-6 px-4">
      <div className="w-full max-w-3xl bg-white border border-gray-200 shadow-md">
        <div className="px-6 py-4">
          <h2 className="text-2xl font-semibold text-[#0F2C5E] mb-3">
            Add Business Information
          </h2>
          {formError && (
            <div className="text-red-500 text-sm mb-3">{formError}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Profile */}
            <section>
              <h3 className="text-lg font-medium text-gray-800 mb-2">
                Business Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Business Name */}
              <div className="relative group">
                <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                  Business Name
                  <div className="relative flex items-center">
                    <HelpCircle
                      size={16}
                      className="text-gray-400 cursor-pointer"
                    />
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      Enter the legal name of your business<br />as registered with the IRS.
                    </div>
                  </div>
                </label>

                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError('');
                  }}
                  className={`w-full p-2 border ${
                    nameError ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                  required
                />

                {nameError && (
                  <p className="text-red-500 text-xs mt-1">{nameError}</p>
                )}
              </div>

                {/* EIN */}
                <div className="relative group">
                  <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                    EIN
                    <div className="relative flex items-center">
                      <HelpCircle
                        size={16}
                        className="text-gray-400 cursor-pointer"
                      />
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Employer Identification Number (e.g. 12-3456789)
                      </div>
                    </div>
                  </label>

                  <input
                    type="text"
                    value={EIN}
                    onChange={handleEINChange}
                    className={`w-full p-2 border ${
                      EINError ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                    required
                  />

                  {EINError && (
                    <p className="text-red-500 text-xs mt-1">{EINError}</p>
                  )}
                </div>

               {/* Business Type */}
                <div className="relative group">
                  <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                    Business Type
                    <div className="relative flex items-center">
                      <HelpCircle
                        size={16}
                        className="text-gray-400 cursor-pointer"
                      />
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Select the legal structure of your<br />business (e.g., LLC or Corporation).
                      </div>
                    </div>
                  </label>

                 <select
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    className="w-full p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm"
                    required
                  >
                    <option value="" disabled>-- Select Business Type --</option>
                    <option value="LLC">LLC</option>
                    <option value="Corporation">Corporation</option>
                    <option value="Sole Proprietor">Sole Proprietor</option>
                    <option value="LP">Limited Partnership </option>
                    <option value="GP">General Partnership</option>
                  </select>
                </div>

                {/* Country */}
                <div className="relative group">
                  <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                    Country
                    <div className="relative flex items-center">
                      <HelpCircle
                        size={16}
                        className="text-gray-400 cursor-pointer"
                      />
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Country where your business is<br />officially registered or operates.
                      </div>
                    </div>
                  </label>

                  <input
                    type="text"
                    value={country}
                    readOnly // если не редактируемое
                    className="bg-gray-100 cursor-not-allowed w-full p-2 border border-gray-300 focus:outline-none text-sm"
                  />

                  {countryError && (
                    <p className="text-red-500 text-xs mt-1">{countryError}</p>
                  )}
                </div>

               {/* Street Address */}
              <div className="relative group">
                <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                  Street Address
                  <div className="relative flex items-center">
                    <HelpCircle
                      size={16}
                      className="text-gray-400 cursor-pointer"
                    />
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      Physical address of your <br />business (no PO Boxes).
                    </div>
                  </div>
                </label>

                <AddressAutocomplete
                  value={streetAddress}
                  onChange={handleStreetChange}
                  onAddressSelected={handleAddressSelected}
                  inputProps={{
                    maxLength: 35,
                    required: true,
                    className: `w-full p-2 border ${
                      streetAddressError ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`,
                  }}
                />
                

                {streetAddressError && (
                  <p className="text-red-500 text-xs mt-1">{streetAddressError}</p>
                )}

                <p className="text-gray-400 text-xs mt-1 text-right">
                  All commonly accepted abbreviations (such as STREET - ST, ROAD - RD, SUITE - STE, etc.) should be used
                  <br></br>
                  {streetAddress.length} / 35
                </p>
              </div>

                {/* State */}
                <div className="relative group">
                  <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                    State
                    <div className="relative flex items-center">
                      <HelpCircle
                        size={16}
                        className="text-gray-400 cursor-pointer"
                      />
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        U.S. state where your <br />business is registered.
                      </div>
                    </div>
                  </label>

                  <select
                    value={state}
                    onChange={(e) => {
                      setState(e.target.value);
                      if (stateError) setStateError('');
                    }}
                    className={`w-full p-2 border ${
                      stateError ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                    required
                  >
                    <option value="">-- Select State --</option>
                    {US_STATES.map((st) => (
                      <option key={st.value} value={st.value}>
                        {st.name}
                      </option>
                    ))}
                  </select>

                  {stateError && (
                    <p className="text-red-500 text-xs mt-1">{stateError}</p>
                  )}
                </div>
                {/* ZIP-Code */}
                <div className="relative group">
                <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                  ZIP Code
                  <div className="relative flex items-center">
                    <HelpCircle
                      size={16}
                      className="text-gray-400 cursor-pointer"
                    />
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      5-digit ZIP or ZIP+4 code
                    </div>
                  </div>
                </label>

                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value);
                    if (zipCodeError) setZipCodeError('');
                  }}
                  className={`w-full p-2 border ${
                    zipCodeError ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                  required
                />

                {zipCodeError && (
                  <p className="text-red-500 text-xs mt-1">{zipCodeError}</p>
                )}
              </div>

               {/* City */}
              <div className="relative group">
                <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                  City
                  <div className="relative flex items-center">
                    <HelpCircle
                      size={16}
                      className="text-gray-400 cursor-pointer"
                    />
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                      City where your business is located.
                    </div>
                  </div>
                </label>

                <input
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    if (cityError) setCityError('');
                  }}
                  className={`w-full p-2 border ${
                    cityError ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                  required
                />

                {cityError && (
                  <p className="text-red-500 text-xs mt-1">{cityError}</p>
                )}
              </div>
              </div>

              {/* USPS address suggestion banner */}
              {uspsSuggestion && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-300 rounded text-sm">
                  <p className="font-semibold text-blue-800 mb-1">USPS suggests a corrected address:</p>
                  <p className="text-blue-700 mb-2">
                    {uspsFullStreet(uspsSuggestion)}, {uspsSuggestion.city}, {uspsSuggestion.state} {uspsSuggestion.ZIPCode}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { const overrides = applyUspsSuggestion(); doSave(overrides); }}
                      className="bg-blue-700 text-white px-3 py-1 text-xs hover:bg-blue-800 transition"
                    >
                      Use USPS Address &amp; Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setUspsSuggestion(null); doSave(); }}
                      className="bg-gray-600 text-white px-3 py-1 text-xs hover:bg-gray-700 transition"
                    >
                      Keep My Address &amp; Save
                    </button>
                  </div>
                </div>
              )}

              {/* USPS apartment/suite not confirmed banner */}
              {uspsAptInvalid && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-300 rounded text-sm">
                  <p className="font-semibold text-orange-800 mb-1">USPS could not confirm the apartment/suite number.</p>
                  <p className="text-orange-700 mb-2">
                    The street address is valid, but the apartment, suite, or unit number you entered
                    was not recognized at this address. Double-check it before saving.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setUspsAptInvalid(false); doSave(); }}
                      className="bg-orange-600 text-white px-3 py-1 text-xs hover:bg-orange-700 transition"
                    >
                      Save Anyway
                    </button>
                    <button
                      type="button"
                      onClick={() => setUspsAptInvalid(false)}
                      className="bg-gray-500 text-white px-3 py-1 text-xs hover:bg-gray-600 transition"
                    >
                      Edit Address
                    </button>
                  </div>
                </div>
              )}

              {/* USPS service unavailable banner */}
              {uspsUnavailable && (
                <div className="mt-3 p-3 bg-gray-50 border border-gray-300 rounded text-sm">
                  <p className="font-semibold text-gray-700 mb-1">USPS address verification is currently unavailable.</p>
                  <p className="text-gray-600 mb-2">Please double-check your address manually before saving.</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setUspsUnavailable(false); doSave(); }}
                      className="bg-gray-700 text-white px-3 py-1 text-xs hover:bg-gray-800 transition"
                    >
                      Save Anyway
                    </button>
                    <button
                      type="button"
                      onClick={() => setUspsUnavailable(false)}
                      className="bg-gray-400 text-white px-3 py-1 text-xs hover:bg-gray-500 transition"
                    >
                      Edit Address
                    </button>
                  </div>
                </div>
              )}

              {/* USPS address not found banner */}
              {uspsNotFound && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-400 rounded text-sm">
                  <p className="font-semibold text-yellow-800 mb-1">USPS could not verify this address.</p>
                  <p className="text-yellow-700 mb-2">The address may still be valid. You can save it as entered.</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setUspsNotFound(false); doSave(); }}
                      className="bg-yellow-600 text-white px-3 py-1 text-xs hover:bg-yellow-700 transition"
                    >
                      Save Anyway
                    </button>
                    <button
                      type="button"
                      onClick={() => setUspsNotFound(false)}
                      className="bg-gray-500 text-white px-3 py-1 text-xs hover:bg-gray-600 transition"
                    >
                      Edit Address
                    </button>
                  </div>
                </div>
              )}
            </section>

              {/* Signing Authority */}
              <section>
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  Signing Authority
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {/* Name */}
                <div className="relative group">
                  <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                    Name
                    <div className="relative flex items-center">
                      <HelpCircle
                        size={16}
                        className="text-gray-400 cursor-pointer"
                      />
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Full name of the person <br />authorized to sign this form.
                      </div>
                    </div>
                  </label>

                  <input
                    type="text"
                    value={signingName}
                    onChange={(e) => {
                      setSigningName(e.target.value);
                      if (signingNameError) setSigningNameError('');
                    }}
                    className={`w-full p-2 border ${
                      signingNameError ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                    required
                  />

                  {signingNameError && (
                    <p className="text-red-500 text-xs mt-1">{signingNameError}</p>
                  )}
                </div>

                   {/* Phone */}
                  <div className="relative group">
                    <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                      Phone
                      <div className="relative flex items-center">
                        <HelpCircle
                          size={16}
                          className="text-gray-400 cursor-pointer"
                        />
                        <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                          Enter the phone number of the <br />signing authority, including area code.
                        </div>
                      </div>
                    </label>

                    <input
                      type="tel"
                      value={signingPhone}
                      onChange={handlePhoneChange}
                      autoComplete="tel"
                      className={`w-full p-2 border ${
                        signingPhoneError ? 'border-red-500' : 'border-gray-300'
                      } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                      required
                    />

                    {signingPhoneError && (
                      <p className="text-red-500 text-xs mt-1">{signingPhoneError}</p>
                    )}
                  </div>

                 {/* PIN */}
                <div className="relative group">
                  <label className="block mb-1 text-sm font-medium flex items-center gap-1">
                    PIN
                    <div className="relative flex items-center">
                      <HelpCircle
                        size={16}
                        className="text-gray-400 cursor-pointer"
                      />
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-normal max-w-[200px] z-10">
                        5-digit PIN used to sign Form 8879-EX. Required if submitting electronically.
                      </div>
                    </div>
                  </label>

                  <input
                    type="password"
                    value={signingPin}
                    autoComplete="off"
                    onChange={(e) => {
                      setSigningPin(e.target.value);
                      if (signingPinError) setSigningPinError('');
                    }}
                    className={`w-full p-2 border ${
                      signingPinError ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                  />

                  {signingPinError && (
                    <p className="text-red-500 text-xs mt-1">{signingPinError}</p>
                  )}
                </div>

                  {/* Job Title */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">Job Title</label>
                    <input
                      type="text"
                      value={signingJobTitle}
                      onChange={(e) => {
                        setSigningJobTitle(e.target.value);
                        if (signingJobTitleError) setSigningJobTitleError('');
                      }}
                      className={`w-full p-2 border ${
                        signingJobTitleError ? 'border-red-500' : 'border-gray-300'
                      } focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] text-sm`}
                      required
                    />
                    {signingJobTitleError && (
                      <p className="text-red-500 text-xs mt-1">{signingJobTitleError}</p>
                    )}
                  </div>
                </div>
              </section>

              {/* Third Party Designee */}
              <section>
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  Third Party Designee
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                  <p>
                    By clicking "Save", you automatically agree to allow another person to discuss this form with the IRS on your behalf.
                  </p>
                </div>
              </section>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className={`px-4 py-2 text-white text-sm transition ${
                    loading || uspsValidating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#0F2C5E] hover:bg-[#0F2C5E]/80'
                  }`}
                  disabled={loading || uspsValidating}
                >
                  {uspsValidating ? 'Verifying address...' : loading ? 'Creating...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default CreateCompany;
